// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTasks } from '../api/tasks';
import type { Task, PageResponse } from '../types';
import { TaskStatus } from '../types';
import { DataTable, Pagination, Badge } from '@loa/ui-components';
import type { DataTableColumn } from '@loa/ui-components';

// ========================================================================
// Task Type config
// ========================================================================

const taskTypeConfig: Record<string, { label: string; color: 'blue' | 'orange' | 'teal' | 'grey' }> = {
  DATA_REQUEST: { label: 'Data request', color: 'blue' },
  COA_REQUEST: { label: 'CoA request', color: 'orange' },
  CHANGE_OF_AGENCY: { label: 'CoA request', color: 'orange' },
  LOS_REQUEST: { label: 'LoS request', color: 'teal' },
};

// ========================================================================
// Task Status config
// ========================================================================

const statusConfig: Record<string, { icon: string; color: string; label: string }> = {
  [TaskStatus.PENDING]:      { icon: '●', color: '#3B82F6', label: 'New request' },
  [TaskStatus.IN_PROGRESS]:  { icon: '◐', color: '#F59E0B', label: 'In progress' },
  [TaskStatus.COMPLETED]:    { icon: '✓', color: '#10B981', label: 'Completed' },
  [TaskStatus.REJECTED]:     { icon: '△', color: '#EF4444', label: 'Rejected' },
  [TaskStatus.CANCELLED]:    { icon: '—', color: '#9CA3AF', label: 'Cancelled' },
};

function TaskStatusCell({ status }: { status: TaskStatus }) {
  const config = statusConfig[status] ?? { icon: '●', color: '#9CA3AF', label: status };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 14 }}>
      <span style={{ color: config.color, fontSize: 14 }}>{config.icon}</span>
      <span style={{ color: config.color, fontWeight: 500 }}>{config.label}</span>
    </span>
  );
}

function formatDate(dateStr: string) {
  const d = new Date(dateStr);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = String(d.getFullYear()).slice(2);
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} @ ${hours}:${minutes}`;
}

function OwnerAvatar({ name }: { name?: string }) {
  const initials = name
    ? name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
    : '?';
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 32,
        height: 32,
        borderRadius: '50%',
        backgroundColor: '#7C3AED',
        color: 'white',
        fontSize: 12,
        fontWeight: 600,
      }}
    >
      {initials}
    </span>
  );
}

// ========================================================================
// Columns
// ========================================================================

function buildColumns(navigate: (path: string) => void): DataTableColumn<Task>[] {
  return [
    {
      key: 'policyTypeName',
      header: 'Policy Type',
      width: '118px',
      render: (task) => <span style={{ fontSize: 14, color: '#333' }}>{task.policyTypeName || '—'}</span>,
    },
    {
      key: 'policyReferenceCode',
      header: 'Policy No.',
      width: '116px',
      render: (task) => (
        <span style={{ fontSize: 14, fontFamily: 'monospace', color: '#333' }}>{task.policyReferenceCode}</span>
      ),
    },
    {
      key: 'brokerageName',
      header: 'Brokerage',
      render: (task) => <span style={{ fontSize: 14, color: '#333' }}>{task.brokerageName}</span>,
    },
    {
      key: 'createdAt',
      header: 'Last updated',
      width: '140px',
      render: (task) => (
        <span style={{ fontSize: 13, color: '#666' }}>{task.updatedAt ? formatDate(task.updatedAt) : formatDate(task.createdAt)}</span>
      ),
    },
    {
      key: 'taskType',
      header: 'Task Type',
      sortable: true,
      width: '134px',
      render: (task) => {
        const config = task.taskType
          ? taskTypeConfig[task.taskType] ?? { label: task.taskType, color: 'grey' as const }
          : { label: 'Unknown', color: 'grey' as const };
        return <Badge color={config.color} size="sm">{config.label}</Badge>;
      },
    },
    {
      key: 'status',
      header: 'Task Status',
      sortable: true,
      width: '170px',
      render: (task) => <TaskStatusCell status={task.status} />,
    },
    {
      key: 'owner',
      header: 'Owner',
      sortable: true,
      width: '101px',
      render: () => <OwnerAvatar name="Provider User" />,
    },
    {
      key: 'actions',
      header: 'Actions',
      width: '101px',
      render: (task) => (
        <button
          onClick={(e) => { e.stopPropagation(); navigate(`/tasks/${task.id}`); }}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: '#7C3AED', fontSize: 18, padding: 4,
          }}
          title="View task"
        >
          👥
        </button>
      ),
    },
  ];
}

// ========================================================================
// Page
// ========================================================================

export function TaskListPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState<PageResponse<Task> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [search, setSearch] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortColumn, setSortColumn] = useState<string | undefined>(undefined);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const fetchTasks = useCallback(() => {
    setLoading(true);
    setError(null);
    const sortParam = sortColumn ? `${sortColumn},${sortDirection}` : 'createdAt,desc';
    getTasks({ page: currentPage, size: pageSize, sort: sortParam, search: searchQuery || undefined })
      .then(setPage)
      .catch(() => setError('Failed to load tasks.'))
      .finally(() => setLoading(false));
  }, [currentPage, pageSize, sortColumn, sortDirection, searchQuery]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
    setCurrentPage(0);
  };

  const handleSearch = () => {
    setSearchQuery(search);
    setCurrentPage(0);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const columns = buildColumns(navigate);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {/* Header + Search */}
      <div style={{ padding: '24px 16px 16px', background: 'white', borderRadius: '12px 12px 0 0', border: '1px solid #E7E7E7', borderBottom: 'none' }}>
        <h1 style={{ fontSize: 20, fontWeight: 600, color: '#141414', marginBottom: 16 }}>Tasks</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            type="text"
            placeholder="Search by or policy type, number or client name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            style={{
              flex: 1, maxWidth: 575, height: 48, padding: '0 16px',
              border: '1px solid #E7E7E7', borderRadius: 8, fontSize: 14,
              outline: 'none', fontFamily: 'Google Sans, sans-serif',
            }}
          />
          <button
            onClick={handleSearch}
            style={{
              height: 48, padding: '0 24px', borderRadius: 8,
              border: '1px solid #2D0A6C', background: 'white',
              color: '#2D0A6C', fontSize: 14, fontWeight: 500,
              cursor: 'pointer', fontFamily: 'Google Sans, sans-serif',
            }}
          >
            Search
          </button>
        </div>
      </div>

      {error && (
        <div style={{ margin: '0 0 8px', padding: '12px 16px', borderRadius: 8, background: '#FEF2F2', border: '1px solid #FECACA', color: '#B91C1C', fontSize: 14 }}>
          {error}
        </div>
      )}

      {/* Table */}
      <DataTable<Task>
        columns={columns}
        data={page?.content ?? []}
        rowKey={(task) => task.id}
        loading={loading}
        skeletonRows={pageSize}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSort={handleSort}
        onRowClick={(task) => navigate(`/tasks/${task.id}`)}
        emptyState={<span style={{ color: '#9CA3AF' }}>No tasks found.</span>}
      />

      {/* Pagination */}
      {page && (
        <Pagination
          currentPage={page.number}
          totalPages={page.totalPages}
          totalItems={page.totalElements}
          itemsPerPage={pageSize}
          itemsPerPageOptions={[5, 10, 20, 50]}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={(size) => { setPageSize(size); setCurrentPage(0); }}
        />
      )}
    </div>
  );
}
